import './account-change-password.css';

import { EventEmitter } from "eventemitter3";
import { EventTypes } from "../../../core/event-bus";
import { Context } from "../../../models/context";
import { Data } from "../../../models/data";
import { LocalizationService } from "../../../services/localization-service";
import { ProfileService } from "../../../services/profile-service";
import { DataLayerService } from '../../../services/data-layer-service';
import { IChangePassword } from "../../../services/profile-service.interface";
import { Modals } from "../../../types/enums";
import { DialogCreators } from "../../../utils/dialog-creators";
import { TwoWayBindingHelper } from "../../../utils/two-way-binding";
import { Util } from "../../../utils/util";
import { LoaderSpinner } from "../../shared/LoaderSpinner/loader-spinner";
import { TimedMessage } from "../../shared/TimedMessage/timed-message";
import { FormController } from "../../shared/Validation/form-controller";
import { ValidationSubmitGuard } from "../../shared/Validation/validation-submit-guard";
import { Modal } from "../modal";

export interface IResetPasswordProps {
    profileIdEncrypted: string;
    messageId: number;
    message: string;
}

export class AccountChangePassword extends Modal {
    private readonly _data: Data;
    private readonly _profileService: ProfileService;
    private _options?: IResetPasswordProps;
    private _item: IChangePassword;
    private _bindingHelper: TwoWayBindingHelper<IChangePassword>;
    private _isResetPassword: boolean;
    private _form!: FormController;

    private readonly _submit: HTMLElement | null;
    private readonly _passwordOld: HTMLInputElement | null;
    private readonly _passwordNew: HTMLInputElement | null;
    private readonly _passwordConfirm: HTMLInputElement | null;

    constructor(eventBus: EventEmitter, data: Data, context: Context, localizationService: LocalizationService, profileService: ProfileService, dataLayer: DataLayerService) {
        super("modal_account_password", eventBus, context, dataLayer, localizationService, { hasCloseButton: true, ariaLabel: "Change Password" }, Modals.ACCOUNT_CHANGE_PASSWORD);
        this._data = data;
        this._profileService = profileService;

        this._submit = document.getElementById("btn_account_password_submit");

        this._passwordOld = document.getElementById("txt_account_password_old") as HTMLInputElement;
        this._passwordNew = document.getElementById("txt_account_password_new") as HTMLInputElement;
        this._passwordConfirm = document.getElementById("txt_account_password_confirm") as HTMLInputElement;

        this._item = {} as IChangePassword;
        this._bindingHelper = new TwoWayBindingHelper(this._item);
        this._isResetPassword = false;
    }

    public override init(): void {
        super.init();
        this._setEventListeners();
        this._setEventBusListener();
        this._setTwoWayListeners();
    }

    public override open(): void {
        super.open();
        this._item = {} as IChangePassword
        this._bindingHelper = new TwoWayBindingHelper(this._item);
        this._setForm();
        this._setTwoWayListeners();
        this._renderModal();
        Util.ensureAnnouncerPresent();
    }

    private _setForm(): void {
        this._form?.destroy();
        this._form = new FormController(this._localizationService, this._dataLayer);

        if (!this._isResetPassword) {
            this._form.register("oldPassword", {
                inputId: "txt_account_password_old",
                errorId: "spn_account_password_old_error",
                rules: [
                    {
                        message: "InvalidPasswordLogin",
                        validate: async (v) => await this._profileService.checkPassword(v),
                        async: true
                    }
                ]
            });
        }

        this._form.register("newPassword", {
            inputId: "txt_account_password_new",
            errorId: "spn_account_password_new_error",
            rules: [
                { message: "InvalidPassword", validate: Util.CheckIsPassword }
            ]
        });

        this._form.register("confirmPassword", {
            inputId: "txt_account_password_confirm",
            errorId: "spn_account_password_confirm_error",
            rules: [
                {
                    message: "InvalidPasswordMatch",
                    validate: v => v === this._passwordNew?.value
                }
            ]
        });
    }

    public override close(): void {
        this._form?.destroy();
        super.close();
    }

    protected readonly _setEventListeners = (): void => {
        this._submit?.addEventListener("click", this._submitPassword);

        const passwordOldShow: HTMLElement | null = document.getElementById("txt_account_password_old_show");
        const passwordOldHide: HTMLElement | null = document.getElementById("txt_account_password_old_hide");
        const passwordNewShow: HTMLElement | null = document.getElementById("txt_account_password_new_show");
        const passwordNewHide: HTMLElement | null = document.getElementById("txt_account_password_new_hide");
        const passwordConfirmShow: HTMLElement | null = document.getElementById("txt_account_password_confirm_show");
        const passwordConfirmHide: HTMLElement | null = document.getElementById("txt_account_password_confirm_hide");

        const showHide = (elementPassword: HTMLInputElement | null, elementShow: HTMLElement | null, elementHide: HTMLElement | null) => {
            elementHide?.addEventListener("click", () => {
                elementPassword?.setAttribute("type", "password")
                elementShow && (elementShow.style.display = "");
                elementHide && (elementHide.style.display = "none");
            })

            elementShow?.addEventListener("click", () => {
                elementPassword?.setAttribute("type", "text")
                elementShow && (elementShow.style.display = "none");
                elementHide && (elementHide.style.display = "");
            })
        }

        showHide(this._passwordOld, passwordOldShow, passwordOldHide);
        showHide(this._passwordNew, passwordNewShow, passwordNewHide);
        showHide(this._passwordConfirm, passwordConfirmShow, passwordConfirmHide);

        passwordOldHide?.click();
        passwordNewHide?.click();
        passwordConfirmHide?.click();
    }

    protected readonly _setEventBusListener = (): void => {
        this._eventBus.on(EventTypes.MODAL_OPEN, (modal: Modals, options: IResetPasswordProps | undefined) => {
            if (modal == Modals.ACCOUNT_CHANGE_PASSWORD) {
                this._options = options;
                this._isResetPassword = !!this._options?.profileIdEncrypted;
            }
        });
    }

    private readonly _setTwoWayListeners = (): void => {
        this._bindingHelper.bindToInput("oldPassword", "txt_account_password_old");
        this._bindingHelper.bindToInput("newPassword", "txt_account_password_new");
    }

    /**
     * Renders the change password or reset password.
     * @private
     * @returns {void}
     */
    private _renderModal = (): void => {
        if (this._isResetPassword && this._options) {
            if (this._options.messageId == 1)
                DialogCreators.messageBoxOk(
                    this._localizationService,
                    this._dataLayer,
                    this._localizationService.names("ResetPass_SessionExpired_Error"),
                    true,
                    () => {
                        this.close();
                        this._eventBus.emit(EventTypes.MODAL_OPEN, Modals.LOGIN);
                    },
                );
            else if (this._options.messageId == 2)
                DialogCreators.messageBoxOk(
                    this._localizationService,
                    this._dataLayer,
                    this._localizationService.names( "ResetPass_Unexpected_Error") + this._options.message,
                    true,
                    () => {
                        this.close();
                        this._eventBus.emit(EventTypes.MODAL_OPEN, Modals.LOGIN);
                    },
                );

            Util.hideElement("current-password-div");
            Util.setElement("ltag", "txt_account_password_new", "ResetPassword");
            Util.setElement("innerHTML", "txt_account_password_new", this._localizationService.names("ResetPassword"));
            Util.setElement("ltag", "btn_account_password_submit", "ResetPassword");
            Util.setElement("innerHTML", "btn_account_password_submit", this._localizationService.names("ResetPassword"));
            Util.showElement("spn_account_password_confirm_error");
            document.getElementById("spn_account_password_confirm_error")?.classList.toggle("error-text", false);

        } else {
            Util.showElement("current-password-div");
            Util.setElement("ltag", "txt_account_password_new", "ChangePassword");
            Util.setElement("innerHTML", "txt_account_password_new", this._localizationService.names("ChangePassword"));
            Util.setElement("ltag", "btn_account_password_submit", "ChangePassword");
            Util.setElement("innerHTML", "btn_account_password_submit", this._localizationService.names("ChangePassword"));
            document.getElementById("spn_account_password_confirm_error")?.classList.toggle("error-text", true);
        }
    }

    private readonly _submitPassword = async () => {
        if (!(await this._guardSubmitValidation())) {
            return;
        }

        const loader = new LoaderSpinner();
        const success = this._isResetPassword
            ? await this._profileService.resetPassword({
                profileId: this._options!.profileIdEncrypted,
                storeKey: this._data.getBrandProperty("StoreInfo")?.storeKey ?? "",
                password: this._item.newPassword
            })
            : await this._profileService.changePassword(this._item);
        loader?.destroy();

        if (success) {
            const wasReset = this._isResetPassword; // capture before you flip it
            this._isResetPassword = false;
            this._options = undefined;
            this.close();

            const message = new TimedMessage(
                wasReset
                    ? this._localizationService.names("PasswordResetSuccessfully")
                    : this._localizationService.names("PasswordUpdatedSuccessfully"),
                "",
                1500
            );
            message.show();

            if (this._dataLayer)
                this._dataLayer.change_password(this._profileService.user.user);
        } else {
            const err = this._localizationService.names("PasswordSaveError");
            const message = new TimedMessage(err, "", 1500);
            message.show();

            Util.announceErrors([err]);
        }
    };

    private readonly _guardSubmitValidation = async (): Promise<boolean> => {
        return await ValidationSubmitGuard.validateAndAnnounce({
            container: "modal_account_password",
            validate: async () => await this._validate()
        });
    }


    private readonly _validate = async (): Promise<boolean> => {
        return await this._form.validate();
    }

    private readonly openLoginModal = () => {
        this._eventBus.emit(EventTypes.MODAL_OPEN, Modals.LOGIN);
    }
}

